import React, { useEffect, useState } from 'react';
import { BarChart3, ShoppingCart, Calendar, TrendingUp, Clock, ChevronRight } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { formatPrice, timeAgo, orderStatusLabel, orderStatusColor } from '../../lib/mockData';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/authStore';
import { normalizeOrder } from '../../lib/backendAdapters';
import { persistence } from '../../lib/persistence';
import type { Order } from '../../types';


const KPICard: React.FC<{ title: string; value: string; sub: string; icon: React.ElementType; color: string }> = ({ title, value, sub, icon: Icon, color }) => (
    <div className="card p-5 flex items-start gap-4 hover:border-white/10 transition-colors">
        <div className={`p-3 rounded-xl ${color}`}>
            <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
            <p className="text-sm text-muted-foreground font-medium">{title}</p>
            <p className="text-2xl font-bold text-white mt-0.5">{value}</p>
            <p className="text-xs text-muted-foreground mt-1">{sub}</p>
        </div>
    </div>
);

const AdminDashboard: React.FC = () => {
    const { user } = useAuthStore();
    const [metrics, setMetrics] = useState<any>(null);
    const [recentOrders, setRecentOrders] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [hasReservationModule, setHasReservationModule] = useState(true);

    useEffect(() => {
        // Carregamento inicial do cache (segurança offline)
        const cachedMetrics = persistence.load<any>('METRICS');
        const cachedOrders = persistence.load<Order[]>('ORDERS');
        
        if (cachedMetrics) setMetrics(cachedMetrics);
        if (cachedOrders) setRecentOrders(cachedOrders.slice(0, 5));

        if (!user?.establishmentId) {
            setLoading(false);
            setError('Nao foi possivel identificar o restaurante da sua conta.');
            return;
        }

        let mounted = true;

        const loadDashboard = async () => {
            setLoading(false); // Já temos dados do cache ou estamos carregando
            setError('');

            try {
                const [metricsResponse, ordersResponse, establishmentResponse] = await Promise.all([
                    api.get(`/dashboard/metrics/${user.establishmentId}`),
                    api.get(`/orders/establishment/${user.establishmentId}`),
                    api.get(`/establishments/id/${user.establishmentId}`),
                ]);

                if (!mounted) return;

                const nextMetrics = metricsResponse.data;
                const nextOrders = ordersResponse.data.slice(0, 5).map(normalizeOrder);

                setMetrics(nextMetrics);
                setRecentOrders(nextOrders);
                setHasReservationModule(establishmentResponse.data?.settings?.hasReservationModule !== false);
                
                // Persistir para segurança offline
                persistence.save('METRICS', nextMetrics);
                persistence.save('ORDERS', ordersResponse.data.map(normalizeOrder)); // Salvamos todos os pedidos no cache
            } catch (err: any) {
                if (!mounted) return;

                console.error(err);
                // Se falhar, mantemos o que veio do cache, só avisamos o erro se não tivermos métricas
                if (!metrics) {
                    setError(
                        err?.response?.data?.message ||
                        'Nao foi possivel carregar o dashboard. Verifique sua conexao.'
                    );
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadDashboard();

        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.establishmentId]);




    if (loading) {
        return <div className="text-white p-6">Carregando painel...</div>;
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="card max-w-2xl p-5 space-y-3">
                    <h1 className="text-xl font-bold text-white">Dashboard indisponivel</h1>
                    <p className="text-sm text-muted-foreground">{error}</p>
                    <p className="text-xs text-muted-foreground">
                        Se voce publicou mudancas recentes de schema no backend, rode <code>npm run db:push</code> no ambiente da API.
                    </p>
                </div>
            </div>
        );
    }

    if (!metrics) {
        return <div className="text-white p-6">Nenhuma metrica disponivel.</div>;
    }
    const m = metrics;

    return (
        <div className="space-y-6 animate-slide-in-up">
            <div>
                <h1 className="text-2xl font-bold text-white">Dashboard</h1>
                <p className="text-muted-foreground text-sm mt-1">Visão geral de hoje, {new Date().toLocaleDateString('pt-BR', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                <KPICard title="Pedidos Hoje" value={String(m?.todayOrders ?? 0)} sub="+12% vs ontem" icon={ShoppingCart} color="bg-primary/20 text-primary" />
                <KPICard title="Faturamento Hoje" value={formatPrice(m?.todayRevenue ?? 0)} sub="+8% vs ontem" icon={TrendingUp} color="bg-success/20 text-success" />
                <KPICard title="Pedidos Pendentes" value={String(m?.pendingOrders ?? 0)} sub="Aguardando ação" icon={Clock} color="bg-warning/20 text-warning" />
                {hasReservationModule && (
                    <KPICard title="Reservas Ativas" value={String(m?.activeReservations ?? 0)} sub="Para hoje e amanhã" icon={Calendar} color="bg-blue-500/20 text-blue-400" />
                )}
            </div>


            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Revenue Chart */}
                <div className="card p-5 lg:col-span-2">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="section-title">Faturamento Semanal</h2>
                            <p className="section-subtitle mt-0.5">Últimos 7 dias</p>
                        </div>
                        <BarChart3 className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={m?.weeklyRevenue || []}>
                            <defs>
                                <linearGradient id="revenue-gradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#F97316" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#F97316" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <XAxis dataKey="day" tick={{ fill: '#64748B', fontSize: 12 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: '#64748B', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `R$${((v || 0) / 1000).toFixed(1)}k`} />
                            <Tooltip contentStyle={{ background: '#111', border: '1px solid #2A2A2A', borderRadius: 12, color: '#fff', fontSize: 12 }} formatter={(v: any) => [formatPrice(v), 'Faturamento']} />
                            <Area type="monotone" dataKey="revenue" stroke="#F97316" strokeWidth={2} fill="url(#revenue-gradient)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>


                {/* Top Products */}
                <div className="card p-5">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="section-title">Mais Vendidos</h2>
                            <p className="section-subtitle mt-0.5">Esta semana</p>
                        </div>
                    </div>
                    <div className="space-y-4">
                        {(m?.topProducts || []).map((p: any, i: number) => (
                            <div key={i} className="space-y-1.5">
                                <div className="flex justify-between text-sm">
                                    <span className="text-white font-medium truncate pr-2">{p.name}</span>
                                    <span className="text-muted-foreground font-bold flex-shrink-0">{p.count}x</span>
                                </div>
                                <div className="w-full h-1.5 bg-\[#1A1A1A\] rounded-full overflow-hidden">
                                    <div className="h-full bg-primary rounded-full" style={{ width: `${(p.count / (m?.topProducts[0]?.count || 1)) * 100}%` }} />
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </div>

            {/* Recent Orders */}
            <div className="card overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-[#2A2A2A]">
                    <h2 className="section-title">Pedidos Recentes</h2>
                    <a href="/admin/pedidos" className="text-sm text-primary hover:text-primary-hover font-medium flex items-center gap-1">
                        Ver todos <ChevronRight className="w-4 h-4" />
                    </a>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-[#2A2A2A] bg-\[#1A1A1A\]/50">
                                <th className="table-header text-left">Pedido</th>
                                <th className="table-header text-left">Cliente</th>
                                <th className="table-header text-left hidden md:table-cell">Tipo</th>
                                <th className="table-header text-left">Status</th>
                                <th className="table-header text-right">Total</th>
                                <th className="table-header text-right hidden sm:table-cell">Horário</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentOrders.map((order) => (
                                <tr key={order.id} className="table-row">
                                    <td className="table-cell font-mono text-muted-foreground text-xs">{order.id.toUpperCase()}</td>
                                    <td className="table-cell font-semibold text-white">{order.customerName}</td>
                                    <td className="table-cell hidden md:table-cell">
                                        <span className="badge-muted capitalize">
                                            {order.type === 'table' ? `Mesa ${order.tableNumber}` : order.type === 'delivery' ? 'Delivery' : 'Balcão'}
                                        </span>
                                    </td>
                                    <td className="table-cell">
                                        <span className={orderStatusColor[order.status]}>
                                            {orderStatusLabel[order.status]}
                                        </span>
                                    </td>
                                    <td className="table-cell text-right font-bold text-white">{formatPrice(order.total)}</td>
                                    <td className="table-cell text-right text-muted-foreground text-xs hidden sm:table-cell">{timeAgo(order.createdAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
